import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import { FormikProvider, useFormik } from "formik";
import {
  OcCard,
  OcCardButtonBar,
  OcConfirmationDialog,
  OcSettingSwitchFormik,
  OcSettingsDivider,
} from "oc/components";
import { useOcCustomerLicences, useOcProperty } from "oc/hooks";
import { useOcFormikHelper } from "oc/utils";

interface MyLeaveGeneralSettingsCardProps {
  isMobile: boolean;
}

export const MyLeaveGeneralSettingsCard = ({
  isMobile,
}: MyLeaveGeneralSettingsCardProps) => {
  const { t } = useTranslation();
  //const [loading, setLoading] = useState(false);
  //const { setOpenMessage } = useOcSnackbar();

  const { licences } = useOcCustomerLicences();
  const myLeaveEmployeeRequestsEnabled = licences?.myLeaveEmployeeRequests;

  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const {
    data: createLeaveRequestIsEnabledProperty,
    loading: createLeaveRequestIsEnabledLoading,
    //refetch: refetchCreateLeaveRequest,
  } = useOcProperty({
    resource: "myLeave",
    key: "createLeaveRequestIsEnabled",
  });

  const {
    data: approveAllLeaveRequestsEnabledData,
    loading: approveAllLeaveRequestsEnabledLoading,
    //refetch: refetchApproveAllLeaveRequests,
  } = useOcProperty({
    resource: "myLeave",
    key: "approveAllLeaveRequestsEnabled",
  });

  const formik = useFormik({
    initialValues: {
      createLeaveRequestIsEnabled: createLeaveRequestIsEnabledProperty === "1",
      approveAllLeaveRequestsEnabled:
        approveAllLeaveRequestsEnabledData === "true",
    },
    onSubmit: () => {
      updateProperties();
    },
    enableReinitialize: true,
  });

  const { loadingState, updateProperties } = useOcFormikHelper({
    defaultResource: "myLeave",
    formik,
    valuesExtraData: {
      createLeaveRequestIsEnabled: {
        key: "createLeaveRequestIsEnabled",
        loading: createLeaveRequestIsEnabledLoading,
        type: "switch",
        returnType: "number",
      },
      approveAllLeaveRequestsEnabled: {
        key: "approveAllLeaveRequestsEnabled",
        loading: approveAllLeaveRequestsEnabledLoading,
        type: "switch",
        returnType: "boolean",
      },
    },
  });

  /*
  const [settings, setSettings] = useState({
    createLeaveRequestIsEnabled: false,
    approveAllLeaveRequestsEnabled: false,
  });

  const [prevSettings, setPrevSettings] = useState(settings);

  useEffect(() => {
    setSettings({
      createLeaveRequestIsEnabled: createLeaveRequestIsEnabledProperty === "1",
      approveAllLeaveRequestsEnabled:
        approveAllLeaveRequestsEnabledData === "true",
    });
    setPrevSettings({
      createLeaveRequestIsEnabled: createLeaveRequestIsEnabledProperty === "1",
      approveAllLeaveRequestsEnabled:
        approveAllLeaveRequestsEnabledData === "true",
    });
  }, [createLeaveRequestIsEnabledProperty, approveAllLeaveRequestsEnabledData]);

  const [updateProperty] = useMutation(OC_UPDATE_PROPERTY, {
    onError() {
      setLoading(false);
      setOpenMessage({
        type: "error",
        message: t("OC_PROPERTY_SAVE_FAILED"),
      });
    },
    onCompleted() {
      refetchCreateLeaveRequest();
      refetchApproveAllLeaveRequests();
      setLoading(false);
      setOpenMessage({
        type: "success",
        message: t("OC_PROPERTY_SAVE_SUCCEEDED"),
      });
    },
  });

  const handleChange =
    (key: keyof typeof settings) =>
      (event: React.ChangeEvent<HTMLInputElement>) => {
        setSettings((prevSettings) => ({
          ...prevSettings,
          [key]: event.target.checked,
        }));
      };
*/
  const handleSave = () => {
    setShowConfirmDialog(false);
    formik.submitForm();
    /*
    setLoading(true);
    const updatePromises = [];

    if (
      settings.createLeaveRequestIsEnabled !==
      prevSettings.createLeaveRequestIsEnabled
    ) {
      updatePromises.push(
        updateProperty({
          variables: {
            resource: "myLeave",
            key: "createLeaveRequestIsEnabled",
            value: settings.createLeaveRequestIsEnabled ? "1" : "0",
          },
        }),
      );
    }

    if (
      settings.approveAllLeaveRequestsEnabled !==
      prevSettings.approveAllLeaveRequestsEnabled
    ) {
      updatePromises.push(
        updateProperty({
          variables: {
            resource: "myLeave",
            key: "approveAllLeaveRequestsEnabled",
            value: settings.approveAllLeaveRequestsEnabled ? "true" : "false",
          },
        }),
      );
    }

    Promise.all(updatePromises)
      .then(() => {
        setPrevSettings(settings);
        setShowConfirmDialog(false);
      })
      .finally(() => {
        setLoading(false);
      });*/
  };

  const handleCancel = () => {
    formik.resetForm();
  };

  const handleDialogCancel = () => {
    setShowConfirmDialog(false);
  };

  return (
    <>
      <OcConfirmationDialog
        showDialog={showConfirmDialog}
        onClose={handleSave}
        onCancel={handleDialogCancel}
        title={t("OC_CONFIRM_SETTING")}
        texts={[t("OC_CONFIRM_SETTING_INFO")]}
        okButtonText={t("OC_ALL_RIGHT")}
        cancelButtonText={t("OC_CANCEL")}
      />
      <OcCard label={t("OC_GENERAL_SETTINGS")} loading={loadingState}>
        <FormikProvider value={formik}>
          <OcSettingSwitchFormik
            name="createLeaveRequestIsEnabled"
            id="createLeaveRequestIsEnabled"
            label={t("MY_LEAVE_ENABLE_LEAVE_REQUESTS")}
            info={t("MY_LEAVE_ENABLE_LEAVE_REQUESTS_INFO")}
            disabled={loadingState || !myLeaveEmployeeRequestsEnabled}
            isMobile={isMobile}
          />
          <Box height={isMobile ? "0px" : "24px"} />
          <OcSettingsDivider />
          <Box height="12px" />
          <OcSettingSwitchFormik
            name="approveAllLeaveRequestsEnabled"
            id="approveAllLeaveRequestsEnabled"
            label={t("MY_TIME_APPROVE_ALL_REQUESTS_ALLOWED")}
            info={t("MY_TIME_APPROVE_ALL_REQUESTS_ALLOWED_INFO")}
            disabled={loadingState || !myLeaveEmployeeRequestsEnabled}
            isMobile={isMobile}
          />
          <OcCardButtonBar
            handleRightButton={handleCancel}
            rightButtonText={t("OC_CANCEL")}
            rightButtonColor="secondary"
            rightButtonId="cancel-general-settings"
            handlePrimaryButton={() => setShowConfirmDialog(true)}
            primaryButtonText={t("OC_SAVE")}
            primaryButtonDisabled={!formik.dirty || loadingState}
            primaryButtonId="save-general-settings"
          />
        </FormikProvider>
      </OcCard>
    </>
  );
};

export default MyLeaveGeneralSettingsCard;
